import { useState, useEffect } from "react";
import axios from "axios";
import { Button, FAQAccordion, Seo } from "../../components";
import StripePaymentModal from "../../components/StripePaymentModal/StripePaymentModal";
import { useAuth } from "../../context/AuthContext";
import {
  PUBLIC_SUBSCRIPTION_PLAN_URL,
  SUBSCRIPTION_PLAN_CENNIK_URL,
  USER_SUBSCRIBE_URL,
  SUBSCRIBE_URL,
  REQUEST_REFUND_URL,
  CREATE_ZERO_SUBSCRIPTION_URL,
  SUBSCRIPTION_PLANS_URL,
} from "../../features/UrlHelper";
import "./CennikPage.scss";
import { formatDateForBackend } from "../../features/dateFormaters";
import { handleApiError } from "../../features/errorHandler";

function PlanSkeleton() {
  return (
    <div className="cennik__skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="cennik__skeleton-card">
          <div className="cennik__skeleton-line cennik__skeleton-line--sm" />
          <div className="cennik__skeleton-line cennik__skeleton-line--lg" />
          <div className="cennik__skeleton-line cennik__skeleton-line--md" />
          <div className="cennik__skeleton-line cennik__skeleton-line--sm" />
          <div className="cennik__skeleton-line cennik__skeleton-line--sm" />
          <div className="cennik__skeleton-line cennik__skeleton-line--sm" />
          <div className="cennik__skeleton-btn" />
        </div>
      ))}
    </div>
  );
}

function CennikPage() {
  const { isLoggedIn, authLoading, userEmail, userId } = useAuth();

  const [plans, setPlans] = useState([]);
  const [fullPlans, setFullPlans] = useState([]); // používa sa na zobrazenie názvu a info o aktívnom predplatnom, načítava sa len pre prihlásených užívateľov obsahuje aj testovacie plány
  const [loading, setLoading] = useState(true);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [userSubscription, setUserSubscription] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // const [cardModes, setCardModes] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (isLoggedIn) {
      loadFullPlans();
      loadUserSubscriptions();
      loadPlans(userId);
    } else if (!authLoading) {
      setLoadingSubscriptions(false);
      loadPlans();
    }
  }, [isLoggedIn, authLoading]);

  async function loadPlans(userId = null) {
    if (userId) {
      await axios
        .get(SUBSCRIPTION_PLAN_CENNIK_URL + `${userId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
          },
        })
        .then((res) => {
          var data = res.data.map((plan) => ({
            ...plan,
            price:
              typeof plan.price === "number"
                ? plan.price
                : parseFloat(plan.price),
          }));
          setPlans(data);
        })
        .catch((err) => {
          setFetchError(handleApiError(err, "Nepodarilo sa načítať plány. Skúste to neskôr."));
        })
        .finally(() => setLoading(false));
    } else {
      await axios
        .get(PUBLIC_SUBSCRIPTION_PLAN_URL)
        .then((res) => {
          var data = res.data.map((plan) => ({
            ...plan,
            price:
              typeof plan.price === "number"
                ? plan.price
                : parseFloat(plan.price),
          }));
          setPlans(data);
        })
        .catch((err) => {
          setFetchError(handleApiError(err, "Nepodarilo sa načítať plány. Skúste to neskôr."));
        })
        .finally(() => setLoading(false));
    }
  }

  async function loadFullPlans() {
    await axios
      .get(SUBSCRIPTION_PLANS_URL, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        var data = res.data.map((plan) => ({
          ...plan,
          price:
            typeof plan.price === "number"
              ? plan.price
              : parseFloat(plan.price),
        }));
        setFullPlans(data);
      })
      .catch((err) => {
        setFetchError(handleApiError(err, "Nepodarilo sa načítať plány. Skúste to neskôr."));
      })
      .finally(() => setLoading(false));
  }

  async function loadUserSubscriptions() {
    await axios
      .get(USER_SUBSCRIBE_URL + `/${userId}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        console.log("User subscription:", res.data);
        setUserSubscription(res.data);
      })
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať používateľské predplatné."))
      .finally(() => setLoadingSubscriptions(false));
  }

  // const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // async function cancelSubscription() {
  //   setCancellingSubscription(true);
  //   await axios
  //     .delete(SUBSCRIBE_URL + `/${userSubscription.id}`, {
  //       headers: {
  //         Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
  //       },
  //     })
  //     .then(() => setUserSubscription(null))
  //     .catch(() => {})
  //     .finally(() => setCancellingSubscription(false));
  // }

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(false);

  async function submitRefundRequest(e) {
    e.preventDefault();
    if (!userSubscription?.stripe_invoice_id || !refundReason.trim()) return;
    setRefundLoading(true);
    await axios
      .post(
        REQUEST_REFUND_URL,
        {
          email: userEmail,
          transactionId: userSubscription.stripe_invoice_id,
          reason: refundReason.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
          },
        },
      )
      .then(() => setRefundSuccess(true))
      .catch((err) => handleApiError(err, { showAlert: true, fallbackMessage: "Chyba pri žiadosti o refundáciu." }))
      .finally(() => setRefundLoading(false));
  }

  // function getCardMode(planId) {
  //   return cardModes[planId] ?? "subscription";
  // }

  // function setCardMode(planId, mode) {
  //   setCardModes((prev) => ({ ...prev, [planId]: mode }));
  // }

  async function handleSelectPlan(plan) {
    setLoadingSubscriptions(true);
    if (plan.test_period) {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var validTo = new Date(today);
      validTo.setDate(validTo.getDate() + 1); /// zatial je 1
      await axios
        .post(
          CREATE_ZERO_SUBSCRIPTION_URL,
          {
            email: userEmail,
            userId: userId,
            validFrom: formatDateForBackend(today),
            validTo: formatDateForBackend(validTo),
            productName: plan.name,
            currency: plan.currency,
            productId: plan.id,
          },
          {
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
            },
          },
        )
        .then(() => {
          loadUserSubscriptions();
          loadPlans(userId);
        })
        .catch((err) => {
          setFetchError(
            handleApiError(err, "Nepodarilo sa aktivovať skúšobné obdobie. Skúste to znova.")
          );
        })
        .finally(() => setLoadingSubscriptions(false));
      return;
    }
    setSelectedPlan(plan);
    setModalOpen(true);
  }

  function formatPrice(plan) {
    if (plan.price == null) return "";
    const amount =
      typeof plan.price === "number" ? plan.price : parseFloat(plan.price);
    const currency = (plan.currency ?? "EUR").toUpperCase();
    return `${amount}${currency === "EUR" ? "€" : ` ${currency}`}`;
  }

  function formatPeriod(plan) {
    const map = {
      month: "/ mesiac",
      monthly: "/ mesiac",
      year: "/ rok",
      yearly: "/ rok",
      annual: "/ rok",
      week: "/ týždeň",
      weekly: "/ týždeň",
    };
    const raw = String(plan.duration ?? "")
      .toLowerCase()
      .trim();
    return map[raw] ?? (raw ? `/ ${raw}` : "/ mesiac");
  }

  return (
    <div className="cennik">
      <Seo
        title="Ceník"
        description="Flexibilné členstvá pre každú úroveň – Capoeira Bratislava. Prvý tréning je vždy zadarmo. Vyberte si mesáčné alebo ročné členstvo."
        canonical="/cennik"
      />
      <section className="cennik__hero">
        <div className="cennik__hero-container">
          <span className="cennik__hero-tag">Cenník</span>
          <h1 className="cennik__hero-title">Investujte do seba</h1>
          <p className="cennik__hero-subtitle">
            Flexibilné členstvá pre každú úroveň.
          </p>
          <p className="cennik__hero-subtitle">
            Prvý tréning je vždy zadarmo.
          </p>
        </div>
      </section>

      <section className="cennik__plans">
        <div className="cennik__plans-container">
          <div className="cennik__plans-header">
            <span className="cennik__tag">Plány</span>
            <h2 className="cennik__plans-title">Vyber si svoj plán</h2>
            <p className="cennik__plans-subtitle">
              Flexibilné členstvá pre každú úroveň
            </p>
            <p className="cennik__plans-free-info">
              Prvý tréning máš úplne zadarmo – príď si vyskúšať bez záväzkov!
              Pre bezplatný tréning nás neváhajte kontaktovať.
            </p>
            <p className="cennik__plans-free-note">
              * Tréner si vyhradzuje právo bezplatný tréning odmietnuť.
            </p>
          </div>

          {isLoggedIn && userSubscription && (
            <div className="cennik__active-sub">
              <div className="cennik__active-sub-left">
                <span className="cennik__active-sub-tag">
                  Aktívne predplatné
                </span>
                <span className="cennik__active-sub-name">
                  {fullPlans.find((p) => p.id === userSubscription.plan_id)
                    ?.name ?? `Plán #${userSubscription.plan_id}`}
                </span>
                {fullPlans.find((p) => p.id === userSubscription.plan_id)
                  ?.test_period ? (
                  <span className="cennik__active-sub-dates">
                    (skúšobné obdobie)
                  </span>
                ) : (
                  <span className="cennik__active-sub-dates">
                    {new Date(userSubscription.valid_from).toLocaleDateString(
                      "sk-SK",
                    )}{" "}
                    —{" "}
                    {new Date(userSubscription.valid_to).toLocaleDateString(
                      "sk-SK",
                    )}
                  </span>
                )}
              </div>
              <div className="cennik__active-sub-actions">
                {/* Zrušenie opakovaného predplatného – zakomentované (len jednorazové platby)
                {userSubscription.repeat ? (
                  <button
                    className="cennik__active-sub-btn cennik__active-sub-btn--cancel"
                    onClick={cancelSubscription}
                    disabled={cancellingSubscription}
                  >
                    Zrušiť predplatné
                  </button>
                ) : (
                  <div className="cennik__active-sub-once-container">
                    <span className="cennik__active-sub-once">
                      Jednorazové predplatné
                    </span>
                  </div>
                )}
                */}
                {fullPlans.find((p) => p.id === userSubscription.plan_id)
                  ?.test_period ? (
                  <></>
                ) : (
                  <button
                    className="cennik__active-sub-btn cennik__active-sub-btn--refund"
                    onClick={() => {
                      setRefundReason("");
                      setRefundSuccess(false);
                      setRefundModalOpen(true);
                    }}
                  >
                    Požiadať o vrátenie
                  </button>
                )}
              </div>
            </div>
          )}

          {loading && <PlanSkeleton />}

          {fetchError && (
            <p className="cennik__error" role="alert">
              {fetchError}
            </p>
          )}

          {!loading && !fetchError && plans.length === 0 && (
            <p className="cennik__empty">
              Momentálne nie sú dostupné žiadne plány.
            </p>
          )}

          {!loading && !fetchError && plans.length > 0 && (
            <div className="cennik__grid">
              {(() => {
                const hasFavorite = plans.some((p) => p.favorite === true);
                const favoriteIndex = hasFavorite
                  ? -1
                  : Math.floor(plans.length / 2);
                const hasActiveSub = Boolean(userSubscription);
                return plans.map((plan, index) => {
                  const isFavorite =
                    plan.favorite === true ||
                    (!hasFavorite && index === favoriteIndex);
                  return (
                    <div
                      key={plan.id}
                      className={`cennik__card${isFavorite ? " cennik__card--favorite" : ""}`}
                    >
                      <div className="cennik__card-body">
                        {isFavorite ? (
                          <div className="cennik__card-badge">
                            <svg
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" />
                            </svg>
                            Najobľúbenejšie
                          </div>
                        ) : null}

                        {/* Toggle Predplatné / Jednorazové – zakomentované (len jednorazové)
                        <div
                          className={`cennik__card-mode-toggle${isFavorite ? " cennik__card-mode-toggle--light" : ""}`}
                          role="group"
                          aria-label="Typ platby"
                        >
                          <button
                            className={`cennik__card-mode-btn${getCardMode(plan.id) === "subscription" ? " cennik__card-mode-btn--active" : ""}`}
                            onClick={() => setCardMode(plan.id, "subscription")}
                            type="button"
                          >
                            Predplatné
                          </button>
                          <button
                            className={`cennik__card-mode-btn${getCardMode(plan.id) === "membership" ? " cennik__card-mode-btn--active" : ""}`}
                            onClick={() => setCardMode(plan.id, "membership")}
                            type="button"
                          >
                            Jednorazové
                          </button>
                        </div>
                        */}

                        <div className="cennik__card-top">
                          <p className="cennik__card-name">{plan.name}</p>
                          <div className="cennik__card-pricing">
                            <span className="cennik__card-price">
                              {formatPrice(plan)}
                            </span>
                            <span className="cennik__card-period">
                              {formatPeriod(plan)}
                            </span>
                          </div>
                          {plan.description && (
                            <p className="cennik__card-description">
                              {plan.description.split("\\n").map((line, i) => (
                                <span key={i}>
                                  {line}
                                  {i <
                                    plan.description.split("\\n").length -
                                    1 && <br />}
                                </span>
                              ))}
                            </p>
                          )}
                        </div>

                        <div className="cennik__card-divider" />
                      </div>

                      <div className="cennik__card-action">
                        <button
                          className={`cennik__card-btn${isFavorite ? " cennik__card-btn--light" : " cennik__card-btn--dark"}`}
                          onClick={() => handleSelectPlan(plan)}
                          disabled={
                            !isLoggedIn || hasActiveSub || loadingSubscriptions
                          }
                          title={
                            !isLoggedIn
                              ? "Pre platbu sa prosím prihláste"
                              : hasActiveSub
                                ? "Už máte aktívne predplatné"
                                : undefined
                          }
                        >
                          {isLoggedIn ? "Začať teraz" : "Prihláste sa"}
                        </button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </section>

      <FAQAccordion
        tag="FAQ"
        title="Otázky k členstvám"
        items={[
          {
            question: "Môžem zmeniť plán kedykoľvek?",
            answer:
              "Áno, plán môžete zmeniť kedykoľvek. Zmena sa prejaví od nasledujúceho zúčtovacieho obdobia.",
          },
          {
            question: "Je prvý tréning naozaj zadarmo?",
            answer:
              "Áno! Prvý tréning je vždy zadarmo a nezáväzný. Stačí sa registrovať online alebo prísť priamo.",
          },
          {
            question: "Aké sú platobné možnosti?",
            answer:
              "Prijímame platby kartou cez Stripe, bankovým prevodom alebo v hotovosti.",
          }
        ]}
      />

      {selectedPlan && (
        <StripePaymentModal
          isOpen={modalOpen}
          onClose={() => { setLoadingSubscriptions(false); setModalOpen(false) }}
          productName={`Plán: ${selectedPlan.name}`}
          productDescription={selectedPlan.description ?? null}
          amount={selectedPlan.price || 0}
          userEmail={userEmail ?? ""}
          currency={selectedPlan.currency ?? "EUR"}
          productId={selectedPlan.id}
          duration={selectedPlan.duration}
          repeatChecked={
            false /* zakomentované: getCardMode(selectedPlan.id) === "subscription" */
          }
          loadUserSubscriptions={loadUserSubscriptions}
        />
      )}

      {/* ── Refund request modal ── */}
      {refundModalOpen && (
        <div
          className="cennik__modal-overlay"
          onClick={() => setRefundModalOpen(false)}
        >
          <div className="cennik__modal" onClick={(e) => e.stopPropagation()}>
            {refundSuccess ? (
              <>
                <h3 className="cennik__modal-title">Žiadosť odoslaná</h3>
                <p className="cennik__modal-text">
                  Vaša žiadosť o vrátenie platby bola odoslaná. Budeme vás
                  kontaktovať na adresu <strong>{userEmail}</strong>.
                </p>
                <div className="cennik__modal-actions">
                  <button
                    className="cennik__modal-btn cennik__modal-btn--primary"
                    onClick={() => setRefundModalOpen(false)}
                  >
                    Zavrieť
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={submitRefundRequest}>
                <h3 className="cennik__modal-title">
                  Žiadosť o vrátenie platby
                </h3>
                <p className="cennik__modal-text">
                  Opíšte dôvod žiadosti o vrátenie platby. Žiadosť bude odoslaná
                  na posúdenie.
                </p>
                <textarea
                  className="cennik__modal-textarea"
                  placeholder="Dôvod žiadosti..."
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={4}
                  maxLength={500}
                  required
                />
                <div className="cennik__modal-actions">
                  <button
                    type="button"
                    className="cennik__modal-btn cennik__modal-btn--ghost"
                    onClick={() => setRefundModalOpen(false)}
                  >
                    Zrušiť
                  </button>
                  <button
                    type="submit"
                    className="cennik__modal-btn cennik__modal-btn--primary"
                    disabled={refundLoading || !refundReason.trim()}
                  >
                    {refundLoading ? "Odosielam..." : "Odoslať žiadosť"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CennikPage;
